import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Stack,
  Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getUsername } from "../../Utility/helper";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import WorkIcon from "@mui/icons-material/Work";

const MyLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const username = getUsername();

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await fetch("https://core-bankers-backend.vercel.app/get-leads", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username }),
        });

        const data = await res.json();
        if (res.ok) setLeads(data.leads);
        else throw new Error(data.message || "Failed to load leads");
      } catch (err) {
        console.error("Fetch Leads Error:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [username]);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        My Leads
      </Typography>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ mt: 6 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading leads...
          </Typography>
        </Stack>
      ) : leads.length === 0 ? (
        <Box textAlign="center" sx={{ mt: 6 }}>
          <Typography variant="h6" color="text.secondary">
            No leads found
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {leads.map((lead) => (
            <Grid item xs={12} sm={6} md={4} key={lead.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  transition: "0.3s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 6px 25px rgba(0,0,0,0.12)" },
                }}
              >
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PersonIcon fontSize="small" /> {lead.name}
                    </Typography>

                    <Typography color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIcon fontSize="small" /> {lead.mobile}
                    </Typography>

                    <Divider />

                    <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <MonetizationOnIcon fontSize="small" color="success" /> Loan:{" "}
                      <strong>{lead.loan_type}</strong> — ₹{lead.amount}
                    </Typography>

                    <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WorkIcon fontSize="small" /> Employment: {lead.employment_type || "—"}
                    </Typography>

                    <Chip
                      label={lead.docs_uploaded ? "Docs Uploaded" : "Docs Pending"}
                      color={lead.docs_uploaded ? "success" : "warning"}
                      size="small"
                      sx={{ alignSelf: "flex-start" }}
                    />
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                  {!lead.docs_uploaded && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate("/upload-docs", { state: { leadId: lead.id } })}
                    >
                      Upload Docs
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/view-lead/${lead.id}`)}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MyLeads;